
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDate } from './utils/formatters';
import { toast } from '@/hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface PaymentDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  repayment: any;
}

export const PaymentDetailsDialog: React.FC<PaymentDetailsDialogProps> = ({
  open,
  onOpenChange,
  repayment
}) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  
  const handleProcessPayment = () => {
    setShowConfirmation(true);
  };
  
  const confirmPayment = () => {
    // Simulated payment processing
    toast({
      title: "Payment Processed Successfully",
      description: `Payment of ${repayment ? formatCurrency(repayment.amount) : ""} has been processed`,
    });
    setShowConfirmation(false);
    onOpenChange(false);
  };
  
  if (!repayment) return null;
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {repayment.status === 'Paid' ? 'Payment Receipt' : 'Payment Details'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Borrowing</p>
                <p className="font-medium">{repayment.borrowing}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Lender</p>
                <p className="font-medium">{repayment.lender}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Due Date</p>
                <p className="font-medium">{formatDate(repayment.date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium">{repayment.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Principal</p>
                <p className="font-medium">{formatCurrency(repayment.principal || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Interest</p>
                <p className="font-medium">{formatCurrency(repayment.interest || 0)}</p>
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between">
                <div className="font-semibold">Total Amount</div>
                <div className="font-bold">{formatCurrency(repayment.amount)}</div>
              </div>
            </div>
            
            {repayment.status === 'Paid' && (
              <div className="border-t pt-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Date</p>
                    <p className="font-medium">{formatDate(repayment.paidDate || repayment.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Reference Number</p>
                    <p className="font-medium">{repayment.utr || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            {repayment.status !== 'Paid' ? (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                <Button onClick={handleProcessPayment}>
                  Process Payment
                </Button>
              </>
            ) : (
              <Button onClick={() => onOpenChange(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Payment Processing</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to process this payment of {formatCurrency(repayment?.amount || 0)}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmation(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPayment}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
